// Friendly copy for wallet / contract / provider failures. Ethers v6 embeds
// the full transaction (calldata, addresses, stack) in error.message, so a
// raw message must never reach a toast. Mapping keys on error codes — never
// on message text. The full raw error is always logged here, so diagnostics
// stay in the console even though the toast is simplified.

const GENERIC_MESSAGE =
  "Something went wrong sending the transaction. Please try again — if it keeps happening, contact support.";

// Wallets and ethers wrap the underlying failure one or more levels deep
// (err.info.error, err.error, err.cause) — walk the chain so a nested
// code or revert reason is still found. Depth-capped against cycles.
const collectErrorChain = (err) => {
  const chain = [];
  let current = err;
  while (current && chain.length < 5) {
    chain.push(current);
    current = current.info?.error || current.error || current.cause;
  }
  return chain;
};

// Map a web3 error to a user-facing message (a plain string for errorToast).
export const mapWeb3Error = (err) => {
  console.error("[web3]", err);

  const chain = collectErrorChain(err);
  const has = (code) => chain.some((e) => e?.code === code);

  if (has("ACTION_REJECTED") || has(4001)) {
    return "You cancelled the transaction in your wallet.";
  }
  if (has("INSUFFICIENT_FUNDS")) {
    return "Your wallet doesn't have enough POL to cover this transaction (price plus a small network fee).";
  }
  if (has("NONCE_EXPIRED") || has("REPLACEMENT_UNDERPRICED")) {
    return "Your wallet has a pending transaction. Wait for it to finish (or speed it up in MetaMask) and try again.";
  }
  if (has("NETWORK_ERROR") || has("TIMEOUT")) {
    return "We're having trouble reaching the blockchain network. Please try again in a moment.";
  }
  if (has("CALL_EXCEPTION")) {
    // Contract revert reasons are human-written ("Item already sold",
    // "Fiat-only listing", …) — surface them.
    const reason = chain
      .map((e) => e?.reason || e?.revert?.args?.[0])
      .find((r) => typeof r === "string" && r);
    if (reason) {
      return `The transaction was rejected: ${reason}`;
    }
    // No revert data almost always means the wallet is on a chain where the
    // contract has no code (wrong network).
    return "We couldn't prepare this transaction. Please check that your wallet is connected to the Polygon network, then try again.";
  }

  const message = typeof err?.message === "string" ? err.message : "";
  const looksSafe = message.length > 0 && !message.includes("0x");
  const hasAnyCode = chain.some(
    (e) => e?.code !== undefined && e?.code !== null,
  );
  // No provider/ethers code anywhere in the chain: this is an application
  // error whose message was already written for the user — keep it intact.
  if (looksSafe && !hasAnyCode) {
    return message;
  }
  // Unknown code, but a short hex-free message is still safe to show.
  if (looksSafe && message.length < 120) {
    return message;
  }
  return GENERIC_MESSAGE;
};
